import React, { useState, useEffect } from "react";
import { AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem, ListItemIcon, ListItemText, Box, Avatar, Menu, MenuItem, Divider, Chip, Alert, Button as MuiButton } from "@mui/material";
import { Menu as MenuIcon, Dashboard as DashboardIcon, VpnKey as VpnKeyIcon, EnhancedEncryption as VaultIcon, Apps as AppsIcon, Extension as ExtensionIcon, Security as SecurityIcon, Business as BusinessIcon, Logout as LogoutIcon, Email as EmailIcon, VisibilityOff as VisibilityOffIcon, Speed as NetworkMonitorIcon, Tune as TuneIcon, Description as ScriptsIcon, CloudUpload as DataPushIcon, Policy as PolicyIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toggleSidebar } from "../redux/slices/uiSlice";
import { logoutUser } from "../redux/thunks/authThunks";

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sidebarOpen } = useSelector((state) => state.ui);
  const user = useSelector((state) => state.auth.user);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [extensionBanner, setExtensionBanner] = useState(null);

  useEffect(() => {
    // Check if extension is installed by looking for the sidebar toggle it injects
    const checkExtension = () => {
      const hasExtension = !!document.getElementById("automatrix-toggle-button");
      if (!hasExtension) {
        // Fetch latest version from API
        const token = localStorage.getItem("token");
        fetch("/api/extension-download/info", {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((data) => {
            if (data.version) {
              setExtensionBanner({
                type: "install",
                version: data.version,
              });
            }
          })
          .catch(() => {});
      } else {
        setExtensionBanner(null);
      }
    };
    // Delay check to let extension content script inject
    const timer = setTimeout(checkExtension, 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    handleMenuClose();
    dispatch(logoutUser()).then(() => {
      navigate("/login");
    });
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const userRole = user?.role;
  const isAdmin = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN';

  const menuItems = [
    { text: "Apps", icon: <AppsIcon />, path: "/apps" },
    { text: "Clients", icon: <BusinessIcon />, path: "/clients" },
    { text: "Client App Matrix", icon: <AppsIcon />, path: "/clients/matrix" },
    { text: "Credentials", icon: <VaultIcon />, path: "/credential-vault" },
    { text: "All Scripts (flat)", icon: <ScriptsIcon />, path: "/scripts" },
    { text: "Get Extension", icon: <ExtensionIcon />, path: "/extension-download" },
    { text: "App Registry (legacy)", icon: <AppsIcon />, path: "/apps/legacy" },
    { text: "Network Rules", icon: <SecurityIcon />, path: "/network-rules" },
    { text: "OU Compliance", icon: <PolicyIcon />, path: "/compliance/google-ou" },
    { text: "Element Hider", icon: <VisibilityOffIcon />, path: "/element-hider" },
    { text: "Network Monitor", icon: <NetworkMonitorIcon />, path: "/network-monitor" },
    { text: "Filter Simulator", icon: <TuneIcon />, path: "/filter-simulator" },
    // --- Data Push section (DRY-RUN) ---
    { text: "Progress Push (Dry Run)", icon: <DataPushIcon />, path: "/progress-push", section: "Data Push" },
    ...(isAdmin ? [{ text: "Email Intercept", icon: <EmailIcon />, path: "/email-intercept" }] : []),
  ];

  return (
    <Box sx={{ display: "flex", overflowX: "hidden", maxWidth: "100vw" }}>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleToggleSidebar}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Automatrix
          </Typography>
          {user && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" noWrap sx={{ display: { xs: "none", sm: "block" } }}>
                {user.name}
              </Typography>
              <IconButton onClick={handleAvatarClick} size="small" sx={{ p: 0 }}>
                <Avatar
                  src={user.picture || undefined}
                  alt={user.name}
                  sx={{ width: 36, height: 36 }}
                >
                  {!user.picture && getInitials(user.name)}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{ paper: { sx: { minWidth: 220, mt: 1 } } }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2" noWrap>{user.name}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>{user.email}</Typography>
                  {user.role && (
                    <Chip label={user.role} size="small" color="primary" variant="outlined" sx={{ mt: 0.5 }} />
                  )}
                </Box>
                <Divider />
                <MenuItem onClick={handleSignOut}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        open={sidebarOpen}
        onClose={handleToggleSidebar}
        sx={{
          width: 240,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 240,
            boxSizing: "border-box",
          },
        }}
      >
        <Toolbar />
        <List>
          {menuItems.map((item, idx) => {
            const prevSection = idx > 0 ? menuItems[idx - 1].section : undefined;
            const showSection = item.section && item.section !== prevSection;
            return (
              <React.Fragment key={item.text}>
                {showSection && (
                  <>
                    <Divider />
                    <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', letterSpacing: 0.5 }}>
                        {item.section.toUpperCase()}
                      </Typography>
                    </Box>
                  </>
                )}
                <ListItem
                  button
                  onClick={() => {
                    navigate(item.path);
                    handleToggleSidebar();
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: 3,
          width: "100%",
          marginTop: "64px",
        }}
      >
        {extensionBanner && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <MuiButton
                color="inherit"
                size="small"
                onClick={() => navigate("/extension-download")}
              >
                {extensionBanner.type === "install" ? "GET EXTENSION" : "UPDATE"}
              </MuiButton>
            }
            onClose={() => setExtensionBanner(null)}
          >
            {extensionBanner.type === "install"
              ? `Automatrix Chrome Extension v${extensionBanner.version} is available. Install it to run scripts directly in your browser.`
              : `Extension update v${extensionBanner.version} available.`}
          </Alert>
        )}
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
